import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { User } from "../models/User";
import { PageContext } from "./PageContext";
import { saveUser } from "../api/connection";


interface SidebarContextType {
  isCollapsed: boolean;
  toggleDrawer: () => Promise<void>;
  recentSceneId: string;
  updateRecentScene: (sceneId: string) => Promise<void>;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function useSidebarContext(): SidebarContextType {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebarContext must be used within a SidebarProvider");
  }
  return context;
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn, user, setUser } = useContext(PageContext);
  const [isCollapsed, setIsCollapsed] = useState(user?.preferences?.navbarCollapsed ?? false);
  const hasToggled = useRef(false); // Use ref to track manual toggles
  const [recentSceneId, setRecentSceneId] = useState(user?.preferences?.recentSceneId ?? "");


  useEffect(() => {
    if (!hasToggled.current) {
      setIsCollapsed(user?.preferences?.navbarCollapsed ?? hasToggled.current);
    }
    setRecentSceneId(user?.preferences?.recentSceneId ?? "");
  }, [user]);



  const toggleDrawer = async () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);

    if (isLoggedIn && user) {
      try {
        const updatedUser: User = {
          ...user,
          preferences: {
            ...(user.preferences || {}),
            navbarCollapsed: newCollapsedState,
          },
        };
        const res = await saveUser(updatedUser);
        if ("userId" in res && "email" in res && "role" in res) {
          setUser(res);
        } else {
          throw new Error("Invalid user response");
        }
      } catch (error) {
        console.error("Error saving preferences:", error);
        setIsCollapsed(!newCollapsedState);
      }
    }
  };



  const updateRecentScene = async (sceneId: string) => {
    console.log('updateRecentScene called on', window.location.pathname, 'sceneId:', sceneId);

    if (sceneId === recentSceneId) return;

    setRecentSceneId(sceneId);

    const updatedPreferences = {
      navbarCollapsed: user?.preferences?.navbarCollapsed ?? isCollapsed,
      recentSceneId: sceneId,
    };

    if (isLoggedIn && user) {
      try {
        const updatedUser: User = { ...user, preferences: updatedPreferences };
        const res = await saveUser(updatedUser);
        setUser({
          ...res,
          preferences: { ...res.preferences, ...updatedPreferences },
        });
      } catch (error) {
        console.error("Error saving recent scene:", error);
      }
    } else {
      // console.log('Saving to localStorage (for scene preferences only):', updatedPreferences);
      // localStorage.setItem("scenePreferences", JSON.stringify(updatedPreferences));
    }
  };


  return (
    <SidebarContext.Provider
      value={{ isCollapsed, toggleDrawer, recentSceneId, updateRecentScene }}
    >
      {children}
    </SidebarContext.Provider>
  );
}